import dayjs, { extend } from 'dayjs';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';
import { FC } from 'react';

import { TProductItemConfig } from '@/components/modules/orders-page/orders/ui/product-item';
import { dateWaiting } from '@/components/modules/orders-page/utils/date-waiting';
import { EOrderStatus } from '@/types/orders';

import { useTimer } from '@/shared/hooks/use-timer';
import { useTranslate } from '@/shared/hooks/use-translate';
import { DangerIcon } from '@/shared/ui/icons';
import { StatusBadge } from '@/shared/ui/status-badge-order';

import * as Styled from './styled';

extend(duration); // Регистрируем плагин duration
extend(relativeTime); // Регистрируем плагин relativeTime
dayjs.locale('ru');

type TProps = {
    nearestPayDate: string;
    config: TProductItemConfig;
    statusName: string;
    time: string;
    daysLeft?: number;
};

const getDayWord = (days: number) => {
    const dayString = days.toString();

    switch (dayString[dayString.length - 1]) {
        case '1':
            return 'день';

        case '2':
        case '3':
        case '4':
            return 'дня';

        default:
            return 'дней';
    }
};

export const Badges: FC<TProps> = ({ nearestPayDate, config, statusName, time, daysLeft }) => {
    const { t } = useTranslate();

    const { message } = useTimer({
        time,
        onUpdate: dateWaiting,
        timeUpdate: 60,
        shouldWork: config.status === EOrderStatus.Approved,
    });

    switch (config.status) {
        case EOrderStatus.Active:
            return (
                <Styled.Wrapper>
                    <StatusBadge theme="processing">{t('Обрабатываем вашу заявку')} 🥰</StatusBadge>
                </Styled.Wrapper>
            );

        case EOrderStatus.Approved:
            return (
                <Styled.Wrapper>
                    <StatusBadge theme="waiting">
                        {t('Ожидает вас в отделении')} {message}
                    </StatusBadge>
                    {config.isBuyout && <StatusBadge theme="buyout">{t('Товар для выкупа')}</StatusBadge>}
                </Styled.Wrapper>
            );

        case EOrderStatus.Issued: {
            const dayToPayDate = config.termUnit === 2 && dayjs(nearestPayDate, 'YYYY-MM-DD').diff(dayjs(), 'day');

            const dayWord = typeof dayToPayDate === 'number' && !Number.isNaN(dayToPayDate) && getDayWord(dayToPayDate);

            const dayString = dayWord && `${dayToPayDate} ${dayWord} до платежа`;

            return (
                <Styled.Wrapper>
                    {config.termUnit === 1 ? (
                        t('remainDays', { daysLeft: daysLeft === 0 ? 1 : daysLeft })
                    ) : (
                        <Styled.DayToPayDate>{dayString}</Styled.DayToPayDate>
                    )}
                    <StatusBadge theme="success">{t('Активен')}</StatusBadge>
                    {config.isBuyout && <StatusBadge theme="buyout">{t('Товар для выкупа')}</StatusBadge>}
                </Styled.Wrapper>
            );
        }

        case EOrderStatus.Delayed:
            return (
                <Styled.Wrapper>
                    <StatusBadge theme="alert">{t(statusName)}</StatusBadge>

                    {config.isBuyout && <StatusBadge theme="buyout">{t('Товар для выкупа')}</StatusBadge>}
                </Styled.Wrapper>
            );

        case EOrderStatus.Closed:
            return (
                <div>
                    {/* {t('Закрыт')} */}
                    <StatusBadge theme="closed">{t(statusName)}</StatusBadge>
                </div>
            );

        case EOrderStatus.Sent_to_court:
            return (
                <Styled.Wrapper>
                    <StatusBadge theme="alertDark">
                        {/* <DangerIcon /> &nbsp; {t('Передано в суд')} */}
                        <DangerIcon /> &nbsp; {t(statusName)}
                    </StatusBadge>
                    {config.isBuyout && <StatusBadge theme="buyout">{t('Товар для выкупа')}</StatusBadge>}
                </Styled.Wrapper>
            );

        default: {
            return <></>;
        }
    }
};
